/**
 * Tutorial Quests Component for Heat Pump Simulation
 * Provides guided learning through progressive, journey-based quests
 * 
 * @module TutorialQuests
 * @description A comprehensive tutorial system that guides learners through
 * heat pump system design, optimization, and energy efficiency concepts.
 * Each quest represents a learning journey with multiple steps, building
 * upon previous knowledge to develop mastery.
 * 
 * Supports three user types with tailored learning paths:
 * - Installer: Practical installation, commissioning, and hydraulic balancing
 * - Energy Consultant: Building analysis, COP optimization, cost/benefit analysis
 * - Sales Person: Customer communication, efficiency benefits, value proposition
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Typography, Space, Button, Divider, Progress, Tag, Tooltip, Collapse, Select } from "antd";
import { 
  CheckCircleFilled, 
  BookOutlined, 
  TrophyOutlined, 
  ExperimentOutlined,
  RocketOutlined,
  AimOutlined,
  PlayCircleOutlined,
  CloseOutlined,
  MinusOutlined,
  ExpandOutlined,
  QuestionCircleOutlined,
  ToolOutlined,
  CalculatorOutlined,
  TeamOutlined
} from "@ant-design/icons";
import { TutorialSpotlight } from "./TutorialSpotlight";
import { TutorialQuiz } from "./TutorialQuiz";
import { ProfessionalQuiz } from "./ProfessionalQuiz";
import { AssignedSkillQuizModal } from "./AssignedSkillQuizModal";
import { getQuestsForUserType } from "./QuestDefinitions";
import { renderQuestIcon } from "./questIcons";
import { createSimulationMetrics, evaluateConditionAst } from "./trainingProfileUtils";
import styles from "./TutorialQuests.module.css";

const { Title, Paragraph, Text } = Typography;

const RichContent = ({ html, children }) => {
  if (!html) return children || null;
  return <div className={styles.richContentBlock} dangerouslySetInnerHTML={{ __html: html }} />;
};

RichContent.propTypes = {
  html: PropTypes.string,
  children: PropTypes.node,
};

const hasMeaningfulText = (value) => {
  if (typeof value !== 'string') return false;
  return value.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').trim().length > 0;
};

const hasRichContent = (value) => hasMeaningfulText(value);

const hasStepContent = (step) => {
  if (!step || typeof step !== 'object') return false;

  return [
    step.task,
    step.instruction,
    step.detailedGuidance,
    step.successIndicator,
    step.instructionRichContent,
    step.detailedGuidanceRichContent,
    step.successIndicatorRichContent,
  ].some(hasMeaningfulText);
};

const buildConditionLabel = (rule) => {
  if (!rule?.field || !rule?.operator) return 'Condition';
  if (rule.operator === 'between' && Array.isArray(rule.value)) {
    return `${rule.field} between ${rule.value[0]} and ${rule.value[1]}`;
  }
  if (rule.operator?.includes('FieldMultiplier')) {
    return `${rule.field} ${rule.operator} ${rule.compareField} × ${rule.multiplier ?? 1}`;
  }
  return `${rule.field} ${rule.operator} ${rule.value}`;
};

const getConditionLabels = (ast) => {
  if (!ast || typeof ast !== 'object') return [];
  if (Array.isArray(ast.rules)) return ast.rules.flatMap(getConditionLabels);
  return [ast.label || buildConditionLabel(ast)];
};

const normalizeProfileQuest = (quest, index) => ({
  id: quest.id,
  title: quest.title,
  category: quest.category || 'Training profile',
  difficulty: quest.difficulty || 'Beginner',
  estimatedTime: quest.estimatedTime,
  icon: renderQuestIcon(quest.iconKey),
  isChallenge: quest.isChallenge,
  conditionAst: quest.conditionAst,
  learningObjectivesRichContent: quest.learningObjectivesRichContent,
  scenarioRichContent: quest.scenarioRichContent,
  realWorldConnectionRichContent: quest.realWorldConnectionRichContent,
  completionDescriptionRichContent: quest.completionDescriptionRichContent,
  knowledgeSynthesisRichContent: quest.knowledgeSynthesisRichContent,
  practicalTipRichContent: quest.practicalTipRichContent,
  learningObjectives: [],
  scenario: { context: '' },
  completionCriteria: { conditions: getConditionLabels(quest.conditionAst) },
  knowledgeSynthesis: { keyTakeaways: [], formulaHighlight: '' },
  steps: (quest.steps || []).map((step, stepIndex) => ({
    id: step.stepKey || `profile-${quest.id}-${step.id || stepIndex}`,
    task: step.taskTitle,
    instruction: '',
    detailedGuidance: '',
    successIndicator: '',
    instructionRichContent: step.instructionRichContent,
    detailedGuidanceRichContent: step.detailedGuidanceRichContent,
    successIndicatorRichContent: step.successIndicatorRichContent,
    spotlightTarget: step.spotlightTarget,
  })),
  displayIndex: index + 1,
});

const evaluateBuiltInQuestConditionStatus = (userType, metrics) => {
  const {
    isSimulationRunning,
    outdoorTemperature,
    selectedRoomType,
    desiredTemperature,
    supplyTemperature,
    heatOutput,
    totalHeatLoss,
    airChangeRate,
    currentCOP,
    currentTemperature,
    valvePosition,
    wallArea,
    uValue,
  } = metrics;

  const meetsHeatDemand = heatOutput >= totalHeatLoss * 0.9;
  const isLivingRoom = selectedRoomType === "living";
  const isTemperatureStable = Math.abs(currentTemperature - desiredTemperature) <= 2;
  const hasTargetWallArea = wallArea >= 48 && wallArea <= 52;
  const hasTargetWallUValue = uValue >= 0.18 && uValue <= 0.22;

  switch (userType) {
    case "energyConsultant":
      return {
        1: [isSimulationRunning],
        2: [outdoorTemperature <= -10, isLivingRoom, desiredTemperature === 20],
        3: [hasTargetWallArea, hasTargetWallUValue],
        4: [airChangeRate < 1.25],
        5: [supplyTemperature <= 40, meetsHeatDemand],
        6: [currentCOP >= 4.5, isTemperatureStable],
      };
    case "salesPerson":
      return {
        1: [isSimulationRunning],
        2: [isSimulationRunning],
        3: [isLivingRoom, desiredTemperature === 20],
        4: [hasTargetWallArea, hasTargetWallUValue],
        5: [supplyTemperature <= 40, meetsHeatDemand],
        6: [currentCOP >= 4.5, isTemperatureStable],
      };
    case "installer":
    default:
      return {
        1: [isSimulationRunning],
        2: [outdoorTemperature <= -10, isLivingRoom, desiredTemperature === 20],
        3: [supplyTemperature <= 45, meetsHeatDemand],
        4: [valvePosition <= 3 / 7, meetsHeatDemand],
        5: [airChangeRate < 1.25],
        6: [currentCOP >= 4.5, isTemperatureStable],
      };
  }
};



/**
 * Individual Quest Button Component
 * Wrapped with Ant Design Tooltip for better hover experience
 */
const QuestButton = ({ quest, isCompleted, isActive, onClick }) => {
  const tooltipTitle = (
    <div>
      <div>{quest.title}</div>
      {isCompleted && (
        <div style={{ fontSize: 11, color: '#52c41a', marginTop: 4 }}>
          ✓ Completed
        </div>
      )}
    </div>
  );

  return (
    <Tooltip 
      title={tooltipTitle} 
      placement="top"
      color="white"
    >
      <div className={`${styles.questButtonWrapper} ${isCompleted ? styles.questButtonCompleted : ''}`}>
        <button
          className={`${styles.questButton} ${isActive ? styles.active : ""} ${quest.isChallenge ? styles.challenge : ""}`}
          onClick={onClick}
        >
          {quest.isChallenge ? (
            <TrophyOutlined className={styles.challengeIcon} />
          ) : (
            <span className={styles.questNumber}>{quest.displayIndex || quest.id}</span>
          )}
        </button>
      </div>
    </Tooltip>
  );
};

QuestButton.propTypes = {
  quest: PropTypes.object.isRequired,
  isCompleted: PropTypes.bool.isRequired,
  isActive: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

/**
 * Difficulty Badge Component
 */
const DifficultyBadge = ({ difficulty }) => {
  const colorMap = {
    Beginner: "green",
    Anfänger: "green",
    Intermediate: "orange", 
    Mittelstufe: "orange",
    Advanced: "red",
    Fortgeschritten: "red",
    Expert: "purple",
    Experte: "purple"
  };
  return <Tag color={colorMap[difficulty] || "default"}>{difficulty}</Tag>;
};

/**
 * Tutorial Quests Container Component
 * 
 * Provides a progressive learning journey through heat pump system design
 * and optimization. Each quest builds upon previous knowledge.
 */
export const TutorialQuests = ({
  trainingProfile,
  trainingProfileMode,
  // Simulation state for quest completion checking
  isSimulationRunning,
  outdoorTemperature,
  desiredTemperature,
  selectedRoomType,
  selectedCity,
  roofArea,
  wallArea,
  uValue,
  totalArea,
  airChangeRate,
  supplyTemperature,
  heatOutput,
  envelopeLoss,
  ventilationLoss,
  valvePosition,
  currentCOP,
  currentTemperature,
  radiatorType,
  radiatorCount,
  systemPressure,
}) => {
  const { t, i18n } = useTranslation();
  const activeLanguage = i18n.resolvedLanguage || i18n.language;
  const profileMode = trainingProfileMode && !!trainingProfile;
  const profileQuizAssignments = useMemo(
    () => (trainingProfile?.quizAssignments || []).filter(assignment => assignment.isQuizCollectionPublished !== false),
    [trainingProfile]
  );
  
  // User type state - determines which quest track to show
  const [userType, setUserType] = useState("installer");
  
  const [selectedQuest, setSelectedQuest] = useState(null);
  const [panelVisible, setPanelVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  // Journey/Spotlight mode state
  const [isJourneyActive, setIsJourneyActive] = useState(false);
  const [journeyStepIndex, setJourneyStepIndex] = useState(0);
  // Quiz state
  const [showQuiz, setShowQuiz] = useState(false);
  const [showCongrats, setShowCongrats] = useState(false);
  // Professional Quiz state
  const [showProfessionalQuiz, setShowProfessionalQuiz] = useState(false);
  const [showAssignedSkillQuiz, setShowAssignedSkillQuiz] = useState(false);
  // Dragging state - calculate right-side position with mobile-safe fallback
  const getInitialPosition = useCallback(() => {
    const panelWidth = 400;
    const margin = 20;
    const x = Math.max(margin, window.innerWidth - panelWidth - margin);
    return { x, y: 80 };
  }, []);
  const [position, setPosition] = useState(getInitialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);
  const dragStartPos = useRef({ x: 0, y: 0 });

  // Update position on window resize to keep panel visible
  useEffect(() => {
    const handleResize = () => {
      setPosition(prev => {
        const panelWidth = 400;
        const margin = 20;
        const maxX = Math.max(margin, window.innerWidth - panelWidth - margin);
        return { x: Math.min(prev.x, maxX), y: prev.y };
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get quests for the current user type
  const QUESTS = useMemo(() => {
    if (profileMode) {
      return (trainingProfile?.tutorialQuests || []).map(normalizeProfileQuest);
    }

    return getQuestsForUserType(userType, activeLanguage);
  }, [activeLanguage, profileMode, trainingProfile, userType]);

  useEffect(() => {
    if (!selectedQuest) {
      return;
    }

    const localizedQuest = QUESTS.find((quest) => quest.id === selectedQuest.id);

    if (!localizedQuest) {
      setSelectedQuest(null);
      return;
    }

    if (localizedQuest !== selectedQuest) {
      setSelectedQuest(localizedQuest);
    }
  }, [QUESTS, selectedQuest]);

  const simulationMetrics = useMemo(() => createSimulationMetrics({
    isSimulationRunning,
    outdoorTemperature,
    selectedRoomType,
    desiredTemperature,
    supplyTemperature,
    heatOutput,
    envelopeLoss,
    ventilationLoss,
    airChangeRate,
    currentCOP,
    currentTemperature,
    valvePosition,
    radiatorType,
    radiatorCount,
    systemPressure,
    roofArea,
    uValue,
    totalArea,
    wallArea,
    selectedCity,
  }), [
    isSimulationRunning,
    outdoorTemperature,
    selectedRoomType,
    desiredTemperature,
    supplyTemperature,
    heatOutput,
    envelopeLoss,
    ventilationLoss,
    airChangeRate,
    currentCOP,
    currentTemperature,
    valvePosition,
    radiatorType,
    radiatorCount,
    systemPressure,
    roofArea,
    totalArea,
    selectedCity,
    wallArea,
    uValue,
  ]);

  // Calculate individual condition status for each quest (each condition separately)
  // Note: Condition mapping is the same for all user types since quests have similar completion criteria
  const individualConditionStatus = useMemo(() => {
    if (profileMode) {
      return QUESTS.reduce((status, quest) => {
        const evaluation = evaluateConditionAst(quest.conditionAst, simulationMetrics);
        status[quest.id] = evaluation.results.length > 0
          ? evaluation.results.map(result => result.passed)
          : [evaluation.passed];
        return status;
      }, {});
    }

    return evaluateBuiltInQuestConditionStatus(userType, simulationMetrics);
  }, [profileMode, QUESTS, simulationMetrics, userType]);

  // Calculate quest completion status based on simulation state (all conditions must be met)
  const questCompletionStatus = useMemo(() => {
    const status = {};
    QUESTS.forEach(quest => {
      const conditions = individualConditionStatus[quest.id];
      status[quest.id] = conditions ? conditions.every(Boolean) : false;
    });
    return status;
  }, [individualConditionStatus, QUESTS]);

  // Count completed quests
  const completedCount = useMemo(() => {
    return Object.values(questCompletionStatus).filter(Boolean).length;
  }, [questCompletionStatus]);

  // Find the first incomplete quest (for "active" highlighting)
  const activeQuestId = useMemo(() => {
    if (profileMode) {
      return QUESTS.find(quest => !questCompletionStatus[quest.id])?.id || null;
    }

    for (let i = 1; i <= QUESTS.length; i++) {
      if (!questCompletionStatus[i]) return i;
    }
    return null;
  }, [profileMode, questCompletionStatus, QUESTS]);

  // Dragging handlers
  const handleDragStart = useCallback((e) => {
    if (e.target.closest('button') || e.target.closest('a')) return;
    setIsDragging(true);
    dragStartPos.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  }, [position]);

  useEffect(() => {
    const handleDragMove = (e) => {
      if (!isDragging) return;
      const newX = e.clientX - dragStartPos.current.x;
      const newY = e.clientY - dragStartPos.current.y;
      setPosition({
        x: Math.max(0, Math.min(newX, window.innerWidth - 420)),
        y: Math.max(0, Math.min(newY, window.innerHeight - 100))
      });
    };

    const handleDragEnd = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
    }

    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
    };
  }, [isDragging]);

  const handleQuestClick = useCallback((quest) => {
    setSelectedQuest(quest);
    setJourneyStepIndex(0);
    setShowQuiz(false);
    setShowCongrats(false);
    setPanelVisible(true);
    setIsMinimized(false);
  }, []);

  const handleClosePanel = useCallback(() => {
    setPanelVisible(false);
    setSelectedQuest(null);
    setShowQuiz(false);
    setShowCongrats(false);
  }, []);

  // Journey/Tour handlers
  const handleStartJourney = useCallback(() => {
    setJourneyStepIndex(0);
    setIsJourneyActive(true);
    setIsMinimized(true);
  }, []);

  const handleJourneyNext = useCallback(() => {
    if (selectedQuest && journeyStepIndex < selectedQuest.steps.length - 1) {
      setJourneyStepIndex((prev) => prev + 1);
    }
  }, [selectedQuest, journeyStepIndex]);

  const handleJourneyPrevious = useCallback(() => {
    if (journeyStepIndex > 0) {
      setJourneyStepIndex((prev) => prev - 1);
    }
  }, [journeyStepIndex]);

  const handleJourneyClose = useCallback(() => {
    setIsJourneyActive(false);
    setIsMinimized(false);
  }, []);

  const handleJourneyComplete = useCallback(() => {
    setIsJourneyActive(false);
    setIsMinimized(false);
    // Show congratulations and prompt for quiz
    if (selectedQuest && questCompletionStatus[selectedQuest.id]) {
      setShowCongrats(true);
    }
  }, [selectedQuest, questCompletionStatus]);

  // Quiz handlers
  const handleStartQuiz = useCallback(() => {
    if (profileMode) return;
    setShowCongrats(false);
    setShowQuiz(true);
  }, [profileMode]);

  const handleCloseQuiz = useCallback(() => {
    setShowQuiz(false);
  }, []);

  const handleBackToQuest = useCallback(() => {
    setShowQuiz(false);
    setShowCongrats(false);
  }, []);

  const isQuestCompleted = (questId) => questCompletionStatus[questId] || false;

  // Calculate progress percentage
  const progressPercent = QUESTS.length > 0 ? Math.round((completedCount / QUESTS.length) * 100) : 0;

  // Handle user type change
  const handleUserTypeChange = useCallback((value) => {
    setUserType(value);
    // Reset selected quest when switching user types
    setSelectedQuest(null);
    setPanelVisible(false);
    setShowQuiz(false);
    setShowCongrats(false);
  }, []);

  const handleProfileSkillQuizClick = useCallback(() => {
    setShowAssignedSkillQuiz(true);
  }, []);

  const learningObjectives = selectedQuest?.learningObjectives || [];
  const completionConditions = selectedQuest?.completionCriteria?.conditions?.filter(hasMeaningfulText) || [];
  const visibleSteps = (selectedQuest?.steps || []).filter(hasStepContent);
  const hasLearningObjectivesSection = hasRichContent(selectedQuest?.learningObjectivesRichContent)
    || learningObjectives.some(hasMeaningfulText);
  const hasScenarioSection = hasRichContent(selectedQuest?.scenarioRichContent)
    || hasMeaningfulText(selectedQuest?.scenario?.context);
  const hasStepsSection = visibleSteps.length > 0;
  const hasCompletionSection = completionConditions.length > 0;
  const hasKnowledgeSynthesisSection = Boolean(selectedQuest)
    && isQuestCompleted(selectedQuest.id)
    && (
      hasRichContent(selectedQuest?.knowledgeSynthesisRichContent)
      || (selectedQuest?.knowledgeSynthesis?.keyTakeaways || []).some(hasMeaningfulText)
      || hasMeaningfulText(selectedQuest?.knowledgeSynthesis?.formulaHighlight)
    );

  // User type options for the dropdown
  const userTypeOptions = [
    { value: 'installer', label: t('heatPumpSimulation.tutorialQuests.userTypes.installer'), icon: <ToolOutlined /> },
    { value: 'energyConsultant', label: t('heatPumpSimulation.tutorialQuests.userTypes.energyConsultant'), icon: <CalculatorOutlined /> },
    { value: 'salesPerson', label: t('heatPumpSimulation.tutorialQuests.userTypes.salesPerson'), icon: <TeamOutlined /> }
  ];

  return (
    <>
      {/* Quest Buttons Row */}
      <div className={styles.questContainer}>
        {/* User Type Selector */}
        {!profileMode && <div className={styles.userTypeSelector}>
          <Select
            value={userType}
            onChange={handleUserTypeChange}
            size="small"
            style={{ width: 155 }}
            popupMatchSelectWidth={false}
            options={userTypeOptions.map(opt => ({
              value: opt.value,
              label: (
                <Space size={6}>
                  {opt.icon}
                  <span>{opt.label}</span>
                </Space>
              )
            }))}
          />
        </div>}
        
        <div className={styles.questButtons}>
          {QUESTS.map((quest) => (
            <QuestButton
              key={quest.id}
              quest={quest}
              isCompleted={isQuestCompleted(quest.id)}
              isActive={quest.id === activeQuestId}
              onClick={() => handleQuestClick(quest)}
            />
          ))}
        </div>
        <div className={styles.progressSection}>
          <Progress 
            percent={progressPercent} 
            size="small" 
            style={{ width: 60 }}
            strokeColor={{
              '0%': '#1890ff',
              '100%': '#52c41a',
            }}
          />
          <span className={styles.progressText}>
            {completedCount}/{QUESTS.length}
          </span>
        </div>
        {profileMode && profileQuizAssignments.length > 0 && <div className={styles.mindMapSection} style={{ position: 'relative' }}>
          <Tooltip title="Choose an assigned skill quiz" color="white">
            <button
              onClick={handleProfileSkillQuizClick}
              className={styles.mindMapButton}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="4" fill="#fff" />
                <circle cx="5" cy="6" r="2.5" fill="#ffd666" />
                <circle cx="19" cy="6" r="2.5" fill="#95de64" />
                <circle cx="5" cy="18" r="2.5" fill="#ff9c6e" />
                <circle cx="19" cy="18" r="2.5" fill="#b37feb" />
                <path d="M8.5 9.5L6.5 7.5M15.5 9.5L17.5 7.5M8.5 14.5L6.5 16.5M15.5 14.5L17.5 16.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <span>Skill Test</span>
            </button>
          </Tooltip>
        </div>}

        {!profileMode && <div className={styles.mindMapSection}>
          <Tooltip title="Take the skill test to assess your knowledge" color="white">
            <button 
              onClick={() => setShowProfessionalQuiz(true)}
              className={styles.mindMapButton}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Central node */}
                <circle cx="12" cy="12" r="4" fill="#fff" />
                {/* Branch nodes */}
                <circle cx="5" cy="6" r="2.5" fill="#ffd666" />
                <circle cx="19" cy="6" r="2.5" fill="#95de64" />
                <circle cx="5" cy="18" r="2.5" fill="#ff9c6e" />
                <circle cx="19" cy="18" r="2.5" fill="#b37feb" />
                {/* Connecting lines */}
                <path d="M8.5 9.5L6.5 7.5M15.5 9.5L17.5 7.5M8.5 14.5L6.5 16.5M15.5 14.5L17.5 16.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <span>Skill Test</span>
            </button>
          </Tooltip>
        </div>}
      </div>

      {/* Spotlight/Tour Overlay - Active during journey mode */}
      <TutorialSpotlight
        isActive={isJourneyActive}
        quest={selectedQuest}
        currentStepIndex={journeyStepIndex}
        onNext={handleJourneyNext}
        onPrevious={handleJourneyPrevious}
        onClose={handleJourneyClose}
        onComplete={handleJourneyComplete}
        isQuestCompleted={selectedQuest ? isQuestCompleted(selectedQuest.id) : false}
      />

      {/* Floating Quest Panel */}
      {panelVisible && selectedQuest && !isMinimized && (
        <div 
          ref={dragRef}
          className={styles.floatingPanel}
          style={{
            left: position.x,
            top: position.y,
            cursor: isDragging ? 'grabbing' : 'default'
          }}
        >
          {/* Draggable Header */}
          <div 
            className={styles.floatingPanelHeader}
            onMouseDown={handleDragStart}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <Space size="middle">
              {selectedQuest?.icon ? (
                <span style={{ fontSize: 18, color: "#1890ff" }}>{selectedQuest.icon}</span>
              ) : (
                <span className={styles.modalQuestNumber}>{selectedQuest?.id}</span>
              )}
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{selectedQuest?.title}</div>
                <Space size="small" style={{ marginTop: 2 }}>
                  <Tag color="blue" style={{ fontSize: 10, padding: '0 4px' }}>{selectedQuest?.category}</Tag>
                  {selectedQuest && <DifficultyBadge difficulty={selectedQuest.difficulty} />}
                </Space>
              </div>
            </Space>
            <Space size={4}>
              <Tooltip title="Minimize" color="white">
                <Button 
                  type="text" 
                  size="small" 
                  icon={<MinusOutlined />} 
                  onClick={() => setIsMinimized(true)}
                />
              </Tooltip>
              <Tooltip title="Close" color="white">
                <Button 
                  type="text" 
                  size="small" 
                  icon={<CloseOutlined />} 
                  onClick={handleClosePanel}
                />
              </Tooltip>
            </Space>
          </div>

          {/* Panel Content */}
          <div className={styles.floatingPanelContent}>
            {/* Show Quiz */}
            {showQuiz ? (
              <TutorialQuiz
                questId={selectedQuest.id}
                questTitle={selectedQuest.title}
                userType={userType}
                isOpen={showQuiz}
                onClose={handleCloseQuiz}
                onBackToQuest={handleBackToQuest}
              />
            ) : showCongrats ? (
              /* Congratulations Screen */
              <div className={styles.congratsContainer}>
                <div className={styles.congratsSvg}>
                  <svg width="100" height="100" viewBox="0 0 100 100">
                    <defs>
                      <linearGradient id="celebrateGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#52c41a" />
                        <stop offset="100%" stopColor="#73d13d" />
                      </linearGradient>
                    </defs>
                    <circle cx="50" cy="50" r="45" fill="url(#celebrateGrad)" />
                    <path
                      d="M30 50 L45 65 L70 35"
                      stroke="#fff"
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                    {/* Sparkles */}
                    <circle cx="20" cy="20" r="4" fill="#ffd700" className={styles.sparkle1} />
                    <circle cx="80" cy="25" r="3" fill="#ffd700" className={styles.sparkle2} />
                    <circle cx="85" cy="70" r="3" fill="#ffd700" className={styles.sparkle3} />
                    <circle cx="15" cy="75" r="3" fill="#ffd700" className={styles.sparkle2} />
                  </svg>
                </div>
                <Title level={4} style={{ color: '#52c41a', marginTop: 16 }}>
                  🎉 Quest Completed!
                </Title>
                <Paragraph style={{ color: '#595959' }}>
                  Excellent work! You've completed "{selectedQuest.title}".
                  {!profileMode && <><br />Ready to test your knowledge?</>}
                </Paragraph>
                <Space direction="vertical" style={{ width: '100%' }}>
                  {!profileMode && (
                    <Button
                      type="primary"
                      size="large"
                      icon={<QuestionCircleOutlined />}
                      onClick={handleStartQuiz}
                      block
                    >
                      Take the Quiz
                    </Button>
                  )}
                  <Button onClick={handleBackToQuest} block>
                    Review Quest Details
                  </Button>
                </Space>
              </div>
            ) : (
              /* Quest Details */
              <div className={styles.questDetailsContent}>
                {/* Completion Banner - Shown when completed */}
                {isQuestCompleted(selectedQuest.id) && (
                  <div className={styles.completedJourneyBanner}>
                    <TrophyOutlined />
                    <div>
                      <Text strong style={{ fontSize: 14 }}>
                        {selectedQuest.isChallenge 
                          ? "🏆 Challenge Completed!" 
                          : "Quest Completed!"}
                      </Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {selectedQuest.isChallenge 
                          ? "You've demonstrated mastery!"
                          : "Great work!"}
                      </Text>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className={styles.actionButtonsRow}>
                  <button
                    onClick={handleStartJourney}
                    className={styles.startJourneyBtn}
                  >
                    <PlayCircleOutlined className={styles.journeyIcon} />
                    <span className={styles.journeyText}>
                      {isQuestCompleted(selectedQuest.id) ? "Replay" : "Start Journey"}
                    </span>
                  </button>
                  
                  {isQuestCompleted(selectedQuest.id) && !profileMode && (
                    <button
                      onClick={handleStartQuiz}
                      className={styles.startQuizBtn}
                    >
                      <QuestionCircleOutlined className={styles.journeyIcon} />
                      <span className={styles.journeyText}>Take Quiz</span>
                    </button>
                  )}
                </div>

                {/* Learning Objectives */}
                {hasLearningObjectivesSection && (
                  <div className={styles.section}>
                    <Title level={5} style={{ fontSize: 13, marginBottom: 8, textAlign: 'left' }}>
                      <RocketOutlined style={{ marginRight: 6 }} />
                      Learning Objectives
                    </Title>
                    <RichContent html={selectedQuest.learningObjectivesRichContent}>
                      <ul className={styles.objectivesListLeft}>
                        {learningObjectives.filter(hasMeaningfulText).map((objective, idx) => (
                          <li key={idx}>{objective}</li>
                        ))}
                      </ul>
                    </RichContent>
                  </div>
                )}

                {hasScenarioSection && <Divider style={{ margin: '12px 0' }} />}

                {/* Scenario Context */}
                {hasScenarioSection && (
                  <div className={styles.section}>
                    <Title level={5} style={{ fontSize: 13, marginBottom: 8, textAlign: 'left' }}>
                      <ExperimentOutlined style={{ marginRight: 6 }} />
                      Scenario
                    </Title>
                    <div className={styles.scenarioBoxCompact}>
                      <RichContent html={selectedQuest.scenarioRichContent}>
                        <Paragraph style={{ marginBottom: 0, fontSize: 12, textAlign: 'left' }}>
                          {selectedQuest.scenario.context}
                        </Paragraph>
                      </RichContent>
                    </div>
                  </div>
                )}

                {hasStepsSection && <Divider style={{ margin: '12px 0' }} />}

                {/* Steps - Collapsible */}
                {hasStepsSection && (
                  <div className={styles.section}>
                    <Title level={5} style={{ fontSize: 13, marginBottom: 8 }}>
                      <AimOutlined style={{ marginRight: 6 }} />
                      Steps ({visibleSteps.length})
                    </Title>
                    <Collapse 
                      ghost 
                      size="small"
                      className={styles.stepsCollapse}
                      items={visibleSteps.map((step, idx) => {
                        const hasInstruction = hasRichContent(step.instructionRichContent) || hasMeaningfulText(step.instruction);
                        const hasGuidance = hasRichContent(step.detailedGuidanceRichContent) || hasMeaningfulText(step.detailedGuidance);
                        const hasSuccessIndicator = hasRichContent(step.successIndicatorRichContent) || hasMeaningfulText(step.successIndicator);

                        return {
                          key: step.id,
                          label: (
                            <div className={styles.stepCollapseHeader}>
                              <span className={styles.stepPreviewNumber}>{idx + 1}</span>
                              <span className={styles.stepPreviewTask}>{step.task}</span>
                            </div>
                          ),
                          children: (
                            <div className={styles.stepCollapseContent}>
                              {hasInstruction && (
                                <RichContent html={step.instructionRichContent}>
                                  <p className={styles.stepInstruction}>{step.instruction}</p>
                                </RichContent>
                              )}
                              {hasGuidance && (
                                <RichContent html={step.detailedGuidanceRichContent}>
                                  <p className={styles.stepGuidance}>{step.detailedGuidance}</p>
                                </RichContent>
                              )}
                              {hasSuccessIndicator && (
                                <div className={styles.stepSuccessHint}>
                                  <CheckCircleFilled style={{ color: '#52c41a', marginRight: 6 }} />
                                  <RichContent html={step.successIndicatorRichContent}>
                                    <span>{step.successIndicator}</span>
                                  </RichContent>
                                </div>
                              )}
                            </div>
                          )
                        };
                      })}
                    />
                  </div>
                )}

                {/* Completion Criteria */}
                {hasCompletionSection && <Divider style={{ margin: '12px 0' }} />}
                {hasCompletionSection && (
                  <div className={styles.section}>
                    <Title level={5} style={{ fontSize: 13, marginBottom: 8 }}>
                      <CheckCircleFilled style={{ marginRight: 6, color: isQuestCompleted(selectedQuest.id) ? "#52c41a" : "#d9d9d9" }} />
                      Completion
                    </Title>
                    <div className={styles.criteriaBoxCompact}>
                      <ul className={styles.criteriaListCompact}>
                        {completionConditions.map((condition, idx) => {
                          const conditionMet = individualConditionStatus[selectedQuest.id]?.[idx] ?? false;
                          return (
                            <li key={idx}>
                              <CheckCircleFilled style={{ 
                                color: conditionMet ? "#52c41a" : "#d9d9d9",
                                marginRight: 6,
                                fontSize: 12
                              }} />
                              {condition}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Knowledge Synthesis - Only shown when completed */}
                {hasKnowledgeSynthesisSection && (
                  <>
                    <Divider style={{ margin: '12px 0' }} />
                    <div className={styles.section}>
                      <Title level={5} style={{ fontSize: 13, marginBottom: 8 }}>
                        <BookOutlined style={{ marginRight: 6 }} />
                        Key Takeaways
                      </Title>
                      <div className={styles.synthesisBoxCompact}>
                        <RichContent html={selectedQuest.knowledgeSynthesisRichContent}>
                          <ul className={styles.takeawaysList}>
                            {selectedQuest.knowledgeSynthesis.keyTakeaways.filter(hasMeaningfulText).slice(0, 3).map((takeaway, idx) => (
                              <li key={idx}>{takeaway}</li>
                            ))}
                          </ul>
                          {hasMeaningfulText(selectedQuest.knowledgeSynthesis.formulaHighlight) && (
                            <div className={styles.formulaBoxCompact}>
                              <pre className={styles.formulaTextCompact}>
                                {selectedQuest.knowledgeSynthesis.formulaHighlight.split('\n')[0]}
                              </pre>
                            </div>
                          )}
                        </RichContent>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Minimized Floating Tab */}
      {panelVisible && isMinimized && !isJourneyActive && (
        <div 
          className={styles.minimizedTab}
          style={{ left: position.x, top: position.y }}
        >
          <Space size="small">
            {selectedQuest?.icon}
            <Text strong style={{ fontSize: 12 }}>{selectedQuest?.title}</Text>
          </Space>
          <Space size={4}>
            <Tooltip title="Expand" color="white">
              <Button 
                type="text" 
                size="small" 
                icon={<ExpandOutlined />} 
                onClick={() => setIsMinimized(false)}
              />
            </Tooltip>
            <Button 
              type="text" 
              size="small" 
              icon={<CloseOutlined />} 
              onClick={handleClosePanel}
            />
          </Space>
        </div>
      )}

      {/* Professional Quiz Modal */}
      <ProfessionalQuiz 
        isOpen={showProfessionalQuiz}
        onClose={() => setShowProfessionalQuiz(false)}
      />

      <AssignedSkillQuizModal
        isOpen={showAssignedSkillQuiz}
        onClose={() => setShowAssignedSkillQuiz(false)}
        assignments={profileQuizAssignments}
      />
    </>
  );
};

TutorialQuests.propTypes = {
  isSimulationRunning: PropTypes.bool.isRequired,
  outdoorTemperature: PropTypes.number.isRequired,
  desiredTemperature: PropTypes.number.isRequired,
  selectedRoomType: PropTypes.string.isRequired,
  selectedCity: PropTypes.string,
  roofArea: PropTypes.number.isRequired,
  wallArea: PropTypes.number.isRequired,
  uValue: PropTypes.number.isRequired,
  totalArea: PropTypes.number.isRequired,
  airChangeRate: PropTypes.number.isRequired,
  supplyTemperature: PropTypes.number.isRequired,
  heatOutput: PropTypes.number.isRequired,
  envelopeLoss: PropTypes.number.isRequired,
  ventilationLoss: PropTypes.number.isRequired,
  valvePosition: PropTypes.number.isRequired,
  currentCOP: PropTypes.number.isRequired,
  currentTemperature: PropTypes.number.isRequired,
  radiatorType: PropTypes.string.isRequired,
  radiatorCount: PropTypes.number.isRequired,
  systemPressure: PropTypes.number.isRequired,
  trainingProfile: PropTypes.object,
  trainingProfileMode: PropTypes.bool,
};

export default TutorialQuests;
